import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { TextInput, SimpleShowLayout, SelectInput, ReferenceInput, AutocompleteInput, BooleanInput } from 'react-admin';
import { InputLabel, FormControl } from '@material-ui/core';
import { formValueSelector } from 'redux-form';
import Header from '../../../shared/header';
import {
  email,
  required,
  stringValue,
  maxLength,
  minLength,
  validVat,
  validSiretSiren,
  validElectronicAddress,
  validVatVerified,
} from '../../../core/validators';
import { getBusinessConf } from '../../../core/config';
import utils from '../../../shared/utils';
import {
  countryList,
  formatSiretSiren,
  getBusinessCountryCode,
  isSiretCountry,
  isSiretRequired,
  SIRET_LENGTH,
} from '../../../shared/country';
import { sanitizeVat, getVatFormatExample, WORLDWIDE_VAT_MAX_LENGTH } from '../../../helpers/vatValidation';
import useElectronicAddressSync from '../../../hooks/useElectronicAddressSync';
import useSiretCaret from '../../../hooks/useSiretCaret';
import AutocompleteChips from '../../../shared/Autocomplete-chips';
import { isPrivateServicesActive } from '../../../helpers';
import GroupedFields from '../../../shared/custom-fields/GroupedFields';
import useGetInvoiceFrequencyFields from '../../../hooks/useGetInvoiceFrequencyFields';
import { PURCHASE_ORDER_REFERENCE_MAX_LENGTH } from '../constants';

const businessEditFormSelector = formValueSelector('business-details-edit');

const BusinessDetailsUnconnected = ({
  id,
  record,
  resource,
  options,
  privateFleet: { privateFleetDefaultOptions, setPrivateFleetSelection },
  classes,
  translate,
  dispatch,
  isAdmin,
  addressCountryCode,
  billingAddressCountryCode,
  useAsBillingAddress,
  siretSiren,
  electronicAddress,
  purchaseOrderReference,
}) => {
  const { config } = getBusinessConf();
  // Every business identity field follows the billing country, with the main address as fallback,
  // as the back end does.
  const businessCountryCode = getBusinessCountryCode(
    { countryCode: addressCountryCode },
    { countryCode: billingAddressCountryCode },
    useAsBillingAddress
  );
  const showSiret = isSiretCountry(businessCountryCode);
  const vatVerifiedExample = getVatFormatExample(businessCountryCode);
  const languageList =
    config && config.translation && config.translation.invoiceLanguage
      ? config.translation.invoiceLanguage.map(language => ({
          id: language.code,
          name: language.label,
        }))
      : [];
  const role = utils.getRoles(id);

  useElectronicAddressSync({
    formName: 'business-details-edit',
    dispatch,
    businessCountryCode,
    siretSiren,
    electronicAddress,
  });

  const { inputRef: siretInputRef, captureCaret: captureSiretCaret } = useSiretCaret(siretSiren);

  const taxNumberCountry = config && config.taxNumberEnable && (
    <AutocompleteInput
      source="taxNumberCountry"
      choices={countryList}
      className={classes.fieldEdit}
      classes={{ suggestionsContainerOpen: classes.autoCompleteContainer }}
      autoComplete="nope"
      id="Selenium-Business-Edit-Address-Country-Input"
    />
  );

  const communityTaxNumber = config && config.taxNumberEnable && (
    <TextInput
      source="communityTaxNumber"
      validate={[maxLength(20)]}
      className={classes.fieldEdit}
      id="Selenium-Business-Edit-communityTaxNumber-Input"
    />
  );

  const invoiceFrequencyList = [
    {
      id: 'DAILY',
      name: translate(`resources.business.fields.invoiceFrequencies.daily`),
    },
    {
      id: 'MONTHLY',
      name: translate(`resources.business.fields.invoiceFrequencies.monthly`),
    },
  ];

  const privateFleets = isAdmin && isPrivateServicesActive(config) && (
    <AutocompleteChips
      classes={{ root: `${classes.privateFleetsContainer} ${classes.fieldEdit}` }}
      isMulti
      label="resources.business.fields.privateFleetsAvailable"
      options={options}
      defaultValue={privateFleetDefaultOptions}
      placeholder=""
      onChange={selection => setPrivateFleetSelection(selection)}
    />
  );

  const invoiceFrequenciesFields = useGetInvoiceFrequencyFields({
    classes,
    context: 'EDIT',
    invoiceFrequencyList,
  });

  return (
    <SimpleShowLayout resource={resource} record={record}>
      <Header label="resources.business.businessDetails" />
      <TextInput
        source="name"
        validate={[required, stringValue, maxLength(64)]}
        required
        className={classes.fieldEdit}
        id="Selenium-Business-Edit-Name-Input"
      />
      <TextInput
        source="legalName"
        validate={[required, stringValue, maxLength(64)]}
        className={classes.fieldEdit}
        id="Selenium-Business-Edit-legalName-Input"
      />
      <TextInput
        source="phoneNumber"
        validate={[maxLength(15)]}
        className={classes.fieldEdit}
        id="Selenium-Business-Edit-phoneNumber-Input"
      />
      <TextInput
        source="faxNumber"
        validate={[maxLength(15)]}
        className={classes.fieldEdit}
        id="Selenium-Business-Edit-faxNumber-Input"
      />
      <TextInput
        source="email"
        type="email"
        validate={[email, maxLength(124)]}
        className={classes.fieldEdit}
        id="Selenium-Business-Edit-Email-Input"
      />
      <TextInput
        source="vat"
        validate={
          config && config.isCountryCodeVat
            ? [required, minLength(4), maxLength(15), validVat]
            : [required, maxLength(32)]
        }
        required
        className={classes.fieldEdit}
        id="Selenium-Business-Edit-Vat-Input"
      />
      {/* Verified VAT (Factur-X BT-48): optional, structurally validated against the business country. */}
      <TextInput
        source="vatVerified"
        label="resources.business.fields.vatVerified"
        className={classes.fieldEdit}
        validate={[validVatVerified, maxLength(WORLDWIDE_VAT_MAX_LENGTH)]}
        parse={sanitizeVat}
        helperText={
          vatVerifiedExample
            ? translate('resources.business.fields.vatVerifiedHint', { example: vatVerifiedExample })
            : undefined
        }
        options={{ inputProps: { maxLength: WORLDWIDE_VAT_MAX_LENGTH } }}
        id="Selenium-Business-Edit-vatVerified-Input"
      />
      {/* EIN is hidden when the SIRET field is displayed to avoid duplicate business info. */}
      {!showSiret && (
        <TextInput
          source="ein"
          validate={[required, maxLength(32)]}
          required
          className={classes.fieldEdit}
          id="Selenium-Business-Edit-Ein-Input"
        />
      )}
      <TextInput
        source="sdicemCode"
        label="SDI / PEC"
        className={classes.fieldEdit}
        validate={[maxLength(124)]}
        id="Selenium-Business-Edit-sdicemCode-Input"
      />
      {showSiret && (
        <TextInput
          source="siretSiren"
          label="resources.business.fields.siretSiren"
          className={classes.fieldEdit}
          validate={[validSiretSiren, maxLength(SIRET_LENGTH + 1)]}
          required={isSiretRequired(businessCountryCode)}
          format={value => formatSiretSiren(value)}
          parse={value => {
            captureSiretCaret(value);
            return value ? value.replace(/\D/g, '') : value;
          }}
          options={{ inputRef: siretInputRef, inputProps: { maxLength: SIRET_LENGTH + 1 } }}
          id="Selenium-Business-Edit-siretSiren-Input"
        />
      )}
      {/* Electronic address (BT-49): shown only once a SIRET is present in a SIRET country. */}
      {showSiret && Boolean(siretSiren) && (
        <TextInput
          source="electronicAddress"
          label="resources.business.fields.electronicAddress"
          helperText={translate('resources.business.fields.electronicAddressHint')}
          className={classes.fieldEdit}
          validate={[validElectronicAddress, maxLength(40)]}
          required={isSiretRequired(businessCountryCode)}
          options={{ inputProps: { maxLength: 40 } }}
          id="Selenium-Business-Edit-electronicAddress-Input"
        />
      )}
      <TextInput
        source="purchaseOrderReference"
        className={classes.fieldEdit}
        validate={[maxLength(PURCHASE_ORDER_REFERENCE_MAX_LENGTH)]}
        helperText={`${(purchaseOrderReference || '').length}/${PURCHASE_ORDER_REFERENCE_MAX_LENGTH}`}
        options={{ inputProps: { maxLength: PURCHASE_ORDER_REFERENCE_MAX_LENGTH } }}
        id="Selenium-Business-Edit-purchaseOrderReference-Input"
      />
      <ReferenceInput
        label="resources.business.fields.billingGroup.name"
        source="billingGroup.id"
        resource="name"
        reference="billingGroup"
        className={classes.fieldEdit}
      >
        <SelectInput disabled={role === 'owner'} source="name" id="Selenium-Business-Edit-billingGroup-Select" />
      </ReferenceInput>
      <ReferenceInput
        label="resources.business.fields.businessOwner.fullName"
        source="businessOwner.id"
        resource="fullName"
        reference="businessOwner"
        className={classes.fieldEdit}
      >
        <SelectInput source="fullName" optionText="fullName" id="Selenium-Business-Edit-businessOwner-Name-Select" />
      </ReferenceInput>
      <SelectInput
        source="status"
        choices={[
          { id: 'APPROVED', name: 'Approved' },
          { id: 'REJECTED', name: 'Rejected' },
          { id: 'SUSPENDED', name: 'Suspended' },
        ]}
        className={classes.fieldEdit}
        id="Selenium-Business-Edit-Status-Select"
      />
      {taxNumberCountry}
      {communityTaxNumber}
      {privateFleets}
      <SelectInput
        source="locale"
        choices={languageList}
        className={classes.fieldEdit}
        validate={[required, maxLength(5)]}
        required
        id="Selenium-Business-Edit-language-Select"
      />
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '82px', width: '100%' }}>
        <GroupedFields title="Generate invoice" fields={invoiceFrequenciesFields} resource={resource} record={record} />
        <FormControl className={classes.fieldEdit}>
          <InputLabel shrink id="Selenium-Business-Edit-MandatoryTripNotes-Label">
            {translate('resources.business.fields.tripNotes')}
          </InputLabel>
          <BooleanInput
            source="mandatoryTripNotes"
            label="resources.business.fields.mandatory"
            id="Selenium-Business-Edit-MandatoryTripNotes-Input"
            style={{ marginTop: '12px' }}
          />
        </FormControl>
      </div>
    </SimpleShowLayout>
  );
};

BusinessDetailsUnconnected.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({})),
  privateFleet: PropTypes.shape({
    privateFleetDefaultOptions: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ),
    setPrivateFleetSelection: PropTypes.func,
  }),
  classes: PropTypes.shape({}),
  record: PropTypes.shape({}),
  resource: PropTypes.string,
  id: PropTypes.string,
  translate: PropTypes.func.isRequired,
  dispatch: PropTypes.func,
  isAdmin: PropTypes.bool.isRequired,
  addressCountryCode: PropTypes.string,
  billingAddressCountryCode: PropTypes.string,
  useAsBillingAddress: PropTypes.bool,
  siretSiren: PropTypes.string,
  electronicAddress: PropTypes.string,
  purchaseOrderReference: PropTypes.string,
};

const mapStateToProps = state => {
  const fleetId = localStorage.getItem('fleetID');
  return {
    options: state.services.reduce((acc, service) => {
      if (service.visibility === 'PRIVATE' && service.fleetId === fleetId) {
        acc.push({ label: service.name, value: service.id });
      }
      return acc;
    }, []),
    addressCountryCode: businessEditFormSelector(state, 'address.countryCode'),
    billingAddressCountryCode: businessEditFormSelector(state, 'billingAddress.countryCode'),
    useAsBillingAddress: businessEditFormSelector(state, 'useAsBillingAddress'),
    siretSiren: businessEditFormSelector(state, 'siretSiren'),
    electronicAddress: businessEditFormSelector(state, 'electronicAddress'),
    purchaseOrderReference: businessEditFormSelector(state, 'purchaseOrderReference'),
  };
};

export const BusinessDetails = connect(mapStateToProps)(BusinessDetailsUnconnected);

export const BusinessDetailsTest = BusinessDetailsUnconnected;
